'use client';
import React, { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import MasjidAccessibility from '../components/MasjidAccessibility';

// ── Khutbah Archive Data ──
const KHUTBAH_ARCHIVE = [
  { id: 'k1', date: '2025-03-14', title: 'Ramadan: The Month of Taqwa', topic: 'Ramadan', videoId: 'n4_T7NbH84c' },
  { id: 'k2', date: '2025-03-07', title: 'The Rights of the Neighbor', topic: 'Community', videoId: 'n4_T7NbH84c' },
  { id: 'k3', date: '2025-02-28', title: 'Patience in Times of Trial', topic: 'Sabr', videoId: 'n4_T7NbH84c' },
  { id: 'k4', date: '2025-02-21', title: 'Brotherhood in Islam', topic: 'Brotherhood', videoId: 'n4_T7NbH84c' },
  { id: 'k5', date: '2025-02-14', title: 'The Importance of Salah', topic: 'Salah', videoId: 'n4_T7NbH84c' },
  { id: 'k6', date: '2025-02-07', title: 'Zakat and Social Justice', topic: 'Zakat', videoId: 'n4_T7NbH84c' },
  { id: 'k7', date: '2025-01-31', title: 'The Quran as Guidance', topic: 'Quran', videoId: 'n4_T7NbH84c' },
  { id: 'k8', date: '2025-01-24', title: 'Tawbah — Returning to Allah', topic: 'Tawbah', videoId: 'n4_T7NbH84c' },
  { id: 'k9', date: '2025-01-17', title: 'The Muslim Family', topic: 'Family', videoId: 'n4_T7NbH84c' },
  { id: 'k10', date: '2025-01-10', title: 'New Year, New Intentions', topic: 'Niyyah', videoId: 'n4_T7NbH84c' },
  { id: 'k11', date: '2024-12-27', title: 'The Seerah of the Prophet ﷺ', topic: 'Seerah', videoId: 'n4_T7NbH84c' },
  { id: 'k12', date: '2024-12-20', title: 'Gratitude and Shukr', topic: 'Shukr', videoId: 'n4_T7NbH84c' },
];

const TOPICS = ['All', 'Ramadan', 'Community', 'Sabr', 'Brotherhood', 'Salah', 'Zakat', 'Quran', 'Tawbah', 'Family', 'Niyyah', 'Seerah', 'Shukr'];

const COMMUNITY_EVENTS = [
  { title: 'Eid Al-Fitr 2025', desc: 'Community Eid celebration at Masjid Al-Ihsan', videoId: 'n4_T7NbH84c', date: 'March 2025' },
  { title: 'Sunday Feeding Program', desc: 'Weekly community meal and gathering', videoId: 'n4_T7NbH84c', date: 'Ongoing' },
  { title: 'Iftar on Wheels — Ramadan 2025', desc: 'Mobile iftar delivery across Bronzeville', videoId: 'n4_T7NbH84c', date: 'Ramadan 2025' },
  { title: 'IIUA Community Forum', desc: 'Islamic Institute of Urban Affairs community discussion', videoId: 'n4_T7NbH84c', date: '2025' },
];

export default function MediaPageClient() {
  const [archiveSearch, setArchiveSearch] = useState('');
  const [archiveTopic, setArchiveTopic] = useState('All');
  const [activeSection, setActiveSection] = useState<string | null>(null);
  const mainRef = useRef<HTMLDivElement>(null);

  const filteredKhutbahs = KHUTBAH_ARCHIVE.filter(k => {
    const matchSearch = archiveSearch === '' ||
      k.title.toLowerCase().includes(archiveSearch.toLowerCase()) ||
      k.topic.toLowerCase().includes(archiveSearch.toLowerCase());
    const matchTopic = archiveTopic === 'All' || k.topic === archiveTopic;
    return matchSearch && matchTopic;
  });

  const handleKeyDown = (e: React.KeyboardEvent, action: () => void) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      action();
    }
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700&family=Amiri:wght@400;700&display=swap');

        .mih-media-client {
          background: #f5f0e8;
          color: #0f172a;
          font-family: 'DM Sans', sans-serif;
          min-height: 100vh;
          padding-top: 68px;
        }

        /* ── Hero ── */
        .mih-media-hero {
          position: relative;
          background: #0d1f3c;
          border-bottom: 1px solid rgba(180,150,80,0.12);
          padding: 80px 40px 64px;
          overflow: hidden;
        }
        .mih-media-hero-blob1 {
          position: absolute; width: 50vw; height: 50vw;
          max-width: 600px; max-height: 600px;
          top: -20%; right: -10%;
          background: radial-gradient(circle, rgba(30,58,138,0.35) 0%, transparent 70%);
          filter: blur(80px); pointer-events: none;
        }
        .mih-media-hero-blob2 {
          position: absolute; width: 35vw; height: 35vw;
          max-width: 400px; max-height: 400px;
          bottom: -20%; left: -5%;
          background: radial-gradient(circle, rgba(180,150,80,0.15) 0%, transparent 70%);
          filter: blur(100px); pointer-events: none;
        }
        .mih-media-hero-grid {
          position: absolute; inset: 0;
          background-image: linear-gradient(rgba(180,150,80,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(180,150,80,0.04) 1px, transparent 1px);
          background-size: 60px 60px; pointer-events: none;
        }
        .mih-media-hero-inner {
          max-width: 1320px; margin: 0 auto; position: relative; z-index: 2;
        }
        .mih-media-badge {
          display: inline-flex; align-items: center; gap: 6px;
          font-family: monospace; font-size: 8px; letter-spacing: 0.25em;
          text-transform: uppercase; color: rgba(180,150,80,0.6);
          background: rgba(180,150,80,0.08); border: 1px solid rgba(180,150,80,0.2);
          padding: 5px 12px; margin-bottom: 20px;
        }
        .mih-media-h1 {
          font-family: 'Amiri', serif; font-size: clamp(32px, 5vw, 60px);
          font-weight: 700; color: rgba(255,255,255,0.92);
          margin: 0 0 16px; line-height: 1.1;
        }
        .mih-media-subtitle {
          font-size: 16px; color: rgba(255,255,255,0.45);
          max-width: 560px; line-height: 1.75; margin: 0 0 24px;
        }
        .mih-media-hero-ayat {
          border-left: 2px solid rgba(180,150,80,0.3); padding: 10px 16px; max-width: 520px;
        }
        .mih-media-hero-ayat-ar {
          font-family: 'Amiri', serif; font-size: 17px;
          color: rgba(255,255,255,0.25); direction: rtl; display: block; margin-bottom: 5px;
        }
        .mih-media-hero-ayat-trans {
          font-size: 12px; color: rgba(255,255,255,0.3); font-style: italic; line-height: 1.6;
        }

        /* ── Section Nav ── */
        .mih-media-section-nav {
          background: #0d1f3c; border-bottom: 1px solid rgba(180,150,80,0.1);
          position: sticky; top: 68px; z-index: 50;
        }
        .mih-media-section-nav-inner {
          max-width: 1320px; margin: 0 auto; padding: 0 40px;
          display: flex; gap: 0; overflow-x: auto;
          scrollbar-width: none;
        }
        .mih-media-section-nav-inner::-webkit-scrollbar { display: none; }
        .mih-media-nav-btn {
          font-family: monospace; font-size: 9px; letter-spacing: 0.18em;
          text-transform: uppercase; color: rgba(255,255,255,0.35);
          background: none; border: none; border-bottom: 2px solid transparent;
          padding: 16px 18px; cursor: pointer; white-space: nowrap;
          transition: color 0.2s, border-color 0.2s;
        }
        .mih-media-nav-btn:hover { color: rgba(255,255,255,0.7); }
        .mih-media-nav-btn.active {
          color: rgba(180,150,80,0.9); border-bottom-color: rgba(180,150,80,0.6);
        }
        .mih-media-nav-btn:focus-visible {
          outline: 2px solid rgba(212,175,55,0.85); outline-offset: -2px;
        }

        /* ── Sections ── */
        .mih-media-section {
          max-width: 1320px; margin: 0 auto; padding: 64px 40px;
        }
        .mih-media-section + .mih-media-section {
          border-top: 1px solid rgba(30,58,138,0.08);
        }
        .mih-media-section-dark {
          background: #0d1f3c;
          border-top: 1px solid rgba(180,150,80,0.08);
          border-bottom: 1px solid rgba(180,150,80,0.08);
        }
        .mih-media-section-dark .mih-media-section {
          padding: 64px 40px;
        }

        /* ── Section Headers ── */
        .mih-media-sec-label {
          font-family: monospace; font-size: 9px; letter-spacing: 0.25em;
          text-transform: uppercase; color: rgba(26,46,90,0.4);
          margin-bottom: 8px; display: block;
        }
        .mih-media-sec-label-light {
          color: rgba(180,150,80,0.55);
        }
        .mih-media-sec-allah {
          font-family: 'Amiri', serif; font-size: 13px;
          color: rgba(26,46,90,0.3); direction: rtl; display: block;
          margin-bottom: 4px;
        }
        .mih-media-sec-allah-light { color: rgba(180,150,80,0.3); }
        .mih-media-sec-h2 {
          font-family: 'Amiri', serif; font-size: clamp(24px, 4vw, 40px);
          font-weight: 700; color: #0f172a; margin: 0 0 10px;
        }
        .mih-media-sec-h2-light { color: rgba(255,255,255,0.9); }
        .mih-media-sec-body {
          font-size: 15px; color: rgba(15,23,42,0.55);
          line-height: 1.75; max-width: 560px; margin: 0 0 32px;
        }
        .mih-media-sec-body-light { color: rgba(255,255,255,0.4); }
        .mih-media-sec-ayat {
          font-size: 12px; color: rgba(15,23,42,0.35);
          font-style: italic; margin-bottom: 32px;
          border-left: 2px solid rgba(26,46,90,0.15); padding-left: 12px;
        }
        .mih-media-sec-ayat-light {
          color: rgba(255,255,255,0.25);
          border-left-color: rgba(180,150,80,0.25);
        }

        /* ── Featured Video ── */
        .mih-media-featured {
          display: grid; grid-template-columns: 1fr 1fr; gap: 40px;
          align-items: start;
        }
        .mih-media-video-wrap {
          position: relative; padding-bottom: 56.25%; height: 0;
          background: #000; border: 1px solid rgba(180,150,80,0.15);
        }
        .mih-media-video-wrap iframe {
          position: absolute; top: 0; left: 0; width: 100%; height: 100%;
          border: none;
        }
        .mih-media-video-info { padding: 8px 0; }
        .mih-media-video-title {
          font-family: 'Amiri', serif; font-size: 22px; font-weight: 700;
          color: #0f172a; margin: 0 0 10px;
        }
        .mih-media-video-title-light { color: rgba(255,255,255,0.88); }
        .mih-media-video-desc {
          font-size: 14px; color: rgba(15,23,42,0.5); line-height: 1.7; margin: 0 0 16px;
        }
        .mih-media-video-desc-light { color: rgba(255,255,255,0.4); }
        .mih-media-playlist-link {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 10px 20px; background: rgba(30,58,138,0.12);
          border: 1px solid rgba(30,58,138,0.25); color: #1e3a8a;
          font-family: monospace; font-size: 9px; letter-spacing: 0.15em;
          text-transform: uppercase; text-decoration: none;
          transition: all 0.2s;
        }
        .mih-media-playlist-link:hover {
          background: rgba(30,58,138,0.2); border-color: rgba(30,58,138,0.4);
        }
        .mih-media-playlist-link:focus-visible {
          outline: 2px solid rgba(212,175,55,0.85); outline-offset: 3px;
        }
        .mih-media-playlist-link-light {
          background: rgba(180,150,80,0.1); border-color: rgba(180,150,80,0.3);
          color: rgba(180,150,80,0.9);
        }
        .mih-media-playlist-link-light:hover {
          background: rgba(180,150,80,0.18); border-color: rgba(180,150,80,0.5);
        }

        /* ── Archive Search ── */
        .mih-archive-controls {
          display: flex; gap: 12px; flex-wrap: wrap; margin-bottom: 28px;
          align-items: center;
        }
        .mih-archive-search {
          flex: 1; min-width: 200px; max-width: 360px;
          padding: 10px 16px; background: #fff;
          border: 1px solid rgba(30,58,138,0.15); color: #0f172a;
          font-family: 'DM Sans', sans-serif; font-size: 14px;
          outline: none; transition: border-color 0.2s;
        }
        .mih-archive-search:focus {
          border-color: rgba(30,58,138,0.4);
          outline: 2px solid rgba(212,175,55,0.7); outline-offset: 2px;
        }
        .mih-archive-search::placeholder { color: rgba(15,23,42,0.3); }
        .mih-archive-topic-btns {
          display: flex; gap: 6px; flex-wrap: wrap;
        }
        .mih-archive-topic-btn {
          padding: 6px 14px; background: none;
          border: 1px solid rgba(30,58,138,0.15); color: rgba(15,23,42,0.5);
          font-family: monospace; font-size: 8px; letter-spacing: 0.15em;
          text-transform: uppercase; cursor: pointer; transition: all 0.2s;
        }
        .mih-archive-topic-btn:hover {
          border-color: rgba(30,58,138,0.3); color: #1e3a8a;
        }
        .mih-archive-topic-btn.active {
          background: #1e3a8a; border-color: #1e3a8a; color: #fff;
        }
        .mih-archive-topic-btn:focus-visible {
          outline: 2px solid rgba(212,175,55,0.85); outline-offset: 2px;
        }

        /* ── Archive Grid ── */
        .mih-archive-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 16px;
        }
        .mih-archive-card {
          background: #fff; border: 1px solid rgba(30,58,138,0.08);
          padding: 20px; transition: border-color 0.2s, box-shadow 0.2s;
          cursor: pointer;
        }
        .mih-archive-card:hover {
          border-color: rgba(30,58,138,0.2);
          box-shadow: 0 4px 16px rgba(30,58,138,0.06);
        }
        .mih-archive-card:focus-visible {
          outline: 2px solid rgba(212,175,55,0.85); outline-offset: 2px;
        }
        .mih-archive-card-meta {
          display: flex; align-items: center; gap: 8px; margin-bottom: 8px;
        }
        .mih-archive-card-date {
          font-family: monospace; font-size: 9px; letter-spacing: 0.12em;
          color: rgba(15,23,42,0.35); text-transform: uppercase;
        }
        .mih-archive-card-topic {
          font-family: monospace; font-size: 8px; letter-spacing: 0.12em;
          text-transform: uppercase; padding: 2px 8px;
          background: rgba(30,58,138,0.06); color: rgba(30,58,138,0.6);
          border: 1px solid rgba(30,58,138,0.1);
        }
        .mih-archive-card-title {
          font-family: 'Amiri', serif; font-size: 17px; font-weight: 700;
          color: #0f172a; margin: 0 0 8px; line-height: 1.3;
        }
        .mih-archive-card-play {
          display: inline-flex; align-items: center; gap: 6px;
          font-family: monospace; font-size: 8px; letter-spacing: 0.15em;
          text-transform: uppercase; color: rgba(30,58,138,0.5);
          text-decoration: none; transition: color 0.2s;
        }
        .mih-archive-card-play:hover { color: #1e3a8a; }
        .mih-archive-no-results {
          grid-column: 1 / -1; text-align: center; padding: 40px;
          color: rgba(15,23,42,0.35); font-size: 14px;
        }

        /* ── Playlist Grid ── */
        .mih-playlist-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 24px; margin-top: 8px;
        }
        .mih-playlist-card {
          background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.07);
          padding: 0; overflow: hidden; transition: border-color 0.2s;
        }
        .mih-playlist-card:hover { border-color: rgba(180,150,80,0.25); }
        .mih-playlist-card-video {
          position: relative; padding-bottom: 56.25%; height: 0; background: #000;
        }
        .mih-playlist-card-video iframe {
          position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;
        }
        .mih-playlist-card-body { padding: 16px 20px; }
        .mih-playlist-card-title {
          font-family: 'Amiri', serif; font-size: 18px; font-weight: 700;
          color: rgba(255,255,255,0.88); margin: 0 0 6px;
        }
        .mih-playlist-card-desc {
          font-size: 13px; color: rgba(255,255,255,0.4); line-height: 1.6; margin: 0;
        }

        /* ── Community Events ── */
        .mih-events-grid {
          display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          gap: 20px;
        }
        .mih-event-card {
          background: #fff; border: 1px solid rgba(30,58,138,0.08);
          overflow: hidden; transition: border-color 0.2s;
        }
        .mih-event-card:hover { border-color: rgba(30,58,138,0.2); }
        .mih-event-card-video {
          position: relative; padding-bottom: 56.25%; height: 0; background: #0d1f3c;
        }
        .mih-event-card-video iframe {
          position: absolute; top: 0; left: 0; width: 100%; height: 100%; border: none;
        }
        .mih-event-card-body { padding: 16px 20px; }
        .mih-event-card-date {
          font-family: monospace; font-size: 9px; letter-spacing: 0.15em;
          text-transform: uppercase; color: rgba(30,58,138,0.4); margin-bottom: 6px; display: block;
        }
        .mih-event-card-title {
          font-family: 'Amiri', serif; font-size: 18px; font-weight: 700;
          color: #0f172a; margin: 0 0 6px;
        }
        .mih-event-card-desc {
          font-size: 13px; color: rgba(15,23,42,0.5); line-height: 1.6; margin: 0;
        }

        /* ── Submit CTA ── */
        .mih-media-cta-wrap {
          max-width: 1320px; margin: 0 auto; padding: 48px 40px;
        }
        .mih-media-cta-inner {
          background: rgba(26,46,90,0.04); border: 1px solid rgba(26,46,90,0.1);
          padding: 40px; display: flex; align-items: center;
          justify-content: space-between; gap: 24px; flex-wrap: wrap;
        }
        .mih-media-cta-text h3 {
          font-family: 'Amiri', serif; font-size: 26px; color: #0f172a; margin: 0 0 8px;
        }
        .mih-media-cta-text p {
          font-size: 14px; color: rgba(15,23,42,0.5); margin: 0;
        }
        .mih-media-cta-btn {
          display: inline-flex; align-items: center; gap: 8px;
          padding: 12px 24px; background: #1a2e5a; color: #fff;
          font-size: 14px; font-weight: 600; text-decoration: none;
          transition: background 0.2s; white-space: nowrap;
        }
        .mih-media-cta-btn:hover { background: #1d4ed8; }
        .mih-media-cta-btn:focus-visible {
          outline: 2px solid rgba(212,175,55,0.85); outline-offset: 3px;
        }

        @media (max-width: 900px) {
          .mih-media-featured { grid-template-columns: 1fr; }
          .mih-media-section { padding: 40px 20px; }
          .mih-media-section-dark .mih-media-section { padding: 40px 20px; }
          .mih-media-hero { padding: 48px 20px 40px; }
          .mih-media-section-nav-inner { padding: 0 20px; }
          .mih-media-cta-wrap { padding: 32px 20px; }
        }
      `}</style>

      <div className="mih-media-client" role="document">
        {/* Hero */}
        <header className="mih-media-hero" role="banner">
          <div className="mih-media-hero-blob1" aria-hidden="true" />
          <div className="mih-media-hero-blob2" aria-hidden="true" />
          <div className="mih-media-hero-grid" aria-hidden="true" />
          <div className="mih-media-hero-inner">
            <MasjidAccessibility
              breadcrumbs={[
                { label: 'Haal Project', href: '/homepage' },
                { label: 'Masjid Al-Ihsan', href: '/subhaal/masjid-al-ihsan' },
                { label: 'Media' },
              ]}
              pageId="media-main"
            />
            <div className="mih-media-badge" aria-hidden="true">⬡ Masjid Al-Ihsan · Media Archive</div>
            <h1 className="mih-media-h1">Media</h1>
            <p className="mih-media-subtitle">
              Khutbahs, Bonds of Brotherhood talks, Talks &amp; Lectures, and community event recordings from Imam Abdullah Madyun and the Masjid Al-Ihsan community. A living archive of knowledge fisabilillah.
            </p>
            <div className="mih-media-hero-ayat" role="complementary" aria-label="Quranic verse">
              <span className="mih-media-hero-ayat-ar" lang="ar">اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ</span>
              <span className="mih-media-hero-ayat-trans">&ldquo;Read in the name of your Lord who created.&rdquo; — Quran 96:1</span>
            </div>
          </div>
        </header>

        {/* Section Navigation */}
        <nav className="mih-media-section-nav" aria-label="Media sections">
          <div className="mih-media-section-nav-inner" role="list">
            {[
              { id: 'latest-khutbahs', label: '📖 Latest Khutbahs' },
              { id: 'khutbah-archive', label: '🗂 Kutbah Archive' },
              { id: 'talks-lectures', label: '🎙 Talks & Lectures' },
              { id: 'bonds-brotherhood', label: '🤝 Bonds of Brotherhood' },
              { id: 'community-events', label: '🌙 Community Events' },
            ].map(sec => (
              <button
                key={sec.id}
                role="listitem"
                className={`mih-media-nav-btn${activeSection === sec.id ? ' active' : ''}`}
                onClick={() => {
                  setActiveSection(sec.id);
                  document.getElementById(sec.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }}
                onKeyDown={e => handleKeyDown(e, () => {
                  setActiveSection(sec.id);
                  document.getElementById(sec.id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
                })}
                aria-label={`Navigate to ${sec.label} section`}
              >
                {sec.label}
              </button>
            ))}
          </div>
        </nav>

        {/* ── MAIN CONTENT ── */}
        <main id="media-main" ref={mainRef} tabIndex={-1}>

          {/* 1. Latest Khutbahs */}
          <section id="latest-khutbahs" aria-labelledby="latest-khutbahs-heading">
            <div className="mih-media-section">
              <span className="mih-media-sec-label">⬡ AL-ALIM — الْعَلِيم · The All-Knowing</span>
              <p className="mih-media-sec-ayat">
                <span lang="ar">اقْرَأْ بِاسْمِ رَبِّكَ الَّذِي خَلَقَ</span>
                {' '}&ldquo;Read in the name of your Lord who created.&rdquo; — Quran 96:1
              </p>
              <h2 id="latest-khutbahs-heading" className="mih-media-sec-h2">Latest Khutbahs</h2>
              <p className="mih-media-sec-body">
                Weekly Friday Khutbahs delivered by Imam Abdullah Madyun. Each Khutbah bridges the Quran and Sunnah to the lived reality of the Bronzeville community and the South Side of Chicago.
              </p>
              <div className="mih-media-featured">
                <div>
                  <div className="mih-media-video-wrap" role="region" aria-label="Latest Khutbah video">
                    <iframe
                      src="https://www.youtube.com/embed/n4_T7NbH84c?list=PLEQX1fhjQx8Nal72X6Dr2ikYskuzbfboX"
                      title="Latest Khutbah — Masjid Al-Ihsan"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      aria-label="Latest Khutbah video from Masjid Al-Ihsan"
                    />
                  </div>
                </div>
                <div className="mih-media-video-info">
                  <h3 className="mih-media-video-title">Latest Khutbah — Imam Abdullah Madyun</h3>
                  <p className="mih-media-video-desc">
                    Imam Madyun delivers the Friday Khutbah at Masjid Al-Ihsan in Bronzeville, Chicago. Two Jumu&apos;ah prayers are held every Friday — 1st at 1:15 PM and 2nd at 2:15 PM. All are welcome.
                  </p>
                  <a
                    href="https://www.youtube.com/playlist?list=PLEQX1fhjQx8Nal72X6Dr2ikYskuzbfboX"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mih-media-playlist-link"
                    aria-label="View full Khutbah playlist on YouTube (opens in new tab)"
                  >
                    ▶ Full Khutbah Playlist on YouTube
                  </a>
                </div>
              </div>
            </div>
          </section>

          {/* 2. Khutbah Archive */}
          <section id="khutbah-archive" aria-labelledby="khutbah-archive-heading" style={{ background: '#fff', borderTop: '1px solid rgba(30,58,138,0.06)', borderBottom: '1px solid rgba(30,58,138,0.06)' }}>
            <div className="mih-media-section">
              <span className="mih-media-sec-label">⬡ AL-HAFIZ — الْحَفِيظ · The Preserver</span>
              <p className="mih-media-sec-ayat">
                <span lang="ar">إِنَّا نَحْنُ نَزَّلْنَا الذِّكْرَ وَإِنَّا لَهُ لَحَافِظُونَ</span>
                {' '}&ldquo;Indeed, it is We who sent down the Quran and indeed, We will be its guardian.&rdquo; — Quran 15:9
              </p>
              <h2 id="khutbah-archive-heading" className="mih-media-sec-h2">Kutbah Archive</h2>
              <p className="mih-media-sec-body">
                Search and browse the full archive of Khutbahs by date or topic. A living record of the knowledge shared at Masjid Al-Ihsan.
              </p>

              {/* Search & Filter Controls */}
              <div className="mih-archive-controls" role="search" aria-label="Search Khutbah archive">
                <label htmlFor="archive-search" className="sr-only">Search Khutbahs by title or topic</label>
                <input
                  id="archive-search"
                  type="search"
                  className="mih-archive-search"
                  placeholder="Search by title or topic..."
                  value={archiveSearch}
                  onChange={e => setArchiveSearch(e.target.value)}
                  aria-label="Search Khutbahs by title or topic"
                />
                <div className="mih-archive-topic-btns" role="group" aria-label="Filter by topic">
                  {TOPICS.map(topic => (
                    <button
                      key={topic}
                      className={`mih-archive-topic-btn${archiveTopic === topic ? ' active' : ''}`}
                      onClick={() => setArchiveTopic(topic)}
                      aria-pressed={archiveTopic === topic}
                      aria-label={`Filter by topic: ${topic}`}
                    >
                      {topic}
                    </button>
                  ))}
                </div>
              </div>

              {/* Archive Grid */}
              <div
                className="mih-archive-grid"
                role="list"
                aria-label={`Khutbah archive — ${filteredKhutbahs.length} results`}
                aria-live="polite"
              >
                {filteredKhutbahs.length === 0 ? (
                  <div className="mih-archive-no-results" role="status">
                    No Khutbahs found matching your search.
                  </div>
                ) : filteredKhutbahs.map(k => (
                  <article
                    key={k.id}
                    className="mih-archive-card"
                    role="listitem"
                    tabIndex={0}
                    aria-label={`Khutbah: ${k.title}, ${k.date}, topic: ${k.topic}`}
                    onKeyDown={e => handleKeyDown(e, () => window.open(`https://www.youtube.com/watch?v=${k.videoId}&list=PLEQX1fhjQx8Nal72X6Dr2ikYskuzbfboX`, '_blank'))}
                  >
                    <div className="mih-archive-card-meta">
                      <span className="mih-archive-card-date">{k.date}</span>
                      <span className="mih-archive-card-topic">{k.topic}</span>
                    </div>
                    <h3 className="mih-archive-card-title">{k.title}</h3>
                    <a
                      href={`https://www.youtube.com/watch?v=${k.videoId}&list=PLEQX1fhjQx8Nal72X6Dr2ikYskuzbfboX`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mih-archive-card-play"
                      aria-label={`Watch ${k.title} on YouTube (opens in new tab)`}
                    >
                      ▶ Watch on YouTube
                    </a>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* 3. Talks & Lectures */}
          <div className="mih-media-section-dark">
            <section id="talks-lectures" aria-labelledby="talks-lectures-heading">
              <div className="mih-media-section">
                <span className="mih-media-sec-label mih-media-sec-label-light">⬡ AL-HADI — الْهَادِي · The Guide</span>
                <p className="mih-media-sec-ayat mih-media-sec-ayat-light">
                  <span lang="ar">وَإِنَّكَ لَتَهْدِي إِلَىٰ صِرَاطٍ مُّسْتَقِيمٍ</span>
                  {' '}&ldquo;And indeed, you guide to a straight path.&rdquo; — Quran 42:52
                </p>
                <h2 id="talks-lectures-heading" className="mih-media-sec-h2 mih-media-sec-h2-light">Talks &amp; Lectures</h2>
                <p className="mih-media-sec-body mih-media-sec-body-light">
                  The full Talks &amp; Lectures playlist from Imam Madyun and the Masjid Al-Ihsan community — covering Quran, Sunnah, Aqidah, Fiqh, and the lived realities of the Muslim community in Chicago.
                </p>
                <div className="mih-media-featured">
                  <div>
                    <div className="mih-media-video-wrap" role="region" aria-label="Talks and Lectures playlist video">
                      <iframe
                        src="https://www.youtube.com/embed/8a7awUunJ4s?list=PLEQX1fhjQx8NMMAebHEdQX1jJwviDCEi6"
                        title="Talks & Lectures — Masjid Al-Ihsan Playlist"
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        aria-label="Talks and Lectures playlist from Masjid Al-Ihsan"
                      />
                    </div>
                  </div>
                  <div className="mih-media-video-info">
                    <h3 className="mih-media-video-title mih-media-video-title-light">Talks &amp; Lectures Playlist</h3>
                    <p className="mih-media-video-desc mih-media-video-desc-light">
                      The complete Talks &amp; Lectures series from Masjid Al-Ihsan. Imam Madyun addresses the spiritual, intellectual, and communal dimensions of Islam — grounded in Quran and Sunnah and rooted in the Bronzeville community.
                    </p>
                    <a
                      href="https://www.youtube.com/watch?v=8a7awUunJ4s&list=PLEQX1fhjQx8NMMAebHEdQX1jJwviDCEi6&index=1"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mih-media-playlist-link mih-media-playlist-link-light"
                      aria-label="View full Talks and Lectures playlist on YouTube (opens in new tab)"
                    >
                      ▶ Full Playlist on YouTube
                    </a>
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* 4. Bonds of Brotherhood */}
          <section id="bonds-brotherhood" aria-labelledby="bonds-brotherhood-heading">
            <div className="mih-media-section">
              <span className="mih-media-sec-label">⬡ AL-WADUD — الْوَدُود · The Loving</span>
              <p className="mih-media-sec-ayat">
                <span lang="ar">إِنَّمَا الْمُؤْمِنُونَ إِخْوَةٌ</span>
                {' '}&ldquo;The believers are but brothers.&rdquo; — Quran 49:10
              </p>
              <h2 id="bonds-brotherhood-heading" className="mih-media-sec-h2">Bonds of Brotherhood</h2>
              <p className="mih-media-sec-body">
                Recorded talks from the Bonds of Brotherhood series — addressing the spiritual, social, and communal dimensions of brotherhood in Islam. Each talk is grounded in Quran, Sunnah, and the lived reality of the community.
              </p>
              <div className="mih-media-featured">
                <div>
                  <div className="mih-media-video-wrap" role="region" aria-label="Latest Bonds of Brotherhood talk video">
                    <iframe
                      src="https://www.youtube.com/embed/8a7awUunJ4s"
                      title="Bonds of Brotherhood — Latest Talk"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                      aria-label="Latest Bonds of Brotherhood talk from Masjid Al-Ihsan"
                    />
                  </div>
                </div>
                <div className="mih-media-video-info">
                  <h3 className="mih-media-video-title">Latest Bonds of Brotherhood Talk</h3>
                  <p className="mih-media-video-desc">
                    The Bonds of Brotherhood series brings Muslim men together around the Quran, Sunnah, and the lived realities of brotherhood in the Bronzeville community and beyond. These talks address the spiritual, social, and communal dimensions of Islamic brotherhood.
                  </p>
                  <a
                    href="https://www.youtube.com/watch?v=8a7awUunJ4s"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mih-media-playlist-link"
                    aria-label="Watch Bonds of Brotherhood on YouTube (opens in new tab)"
                  >
                    ▶ Watch on YouTube
                  </a>
                </div>
              </div>

              {/* Additional Bonds talks grid */}
              <div style={{ marginTop: 40 }}>
                <h3 style={{ fontFamily: "'Amiri', serif", fontSize: 22, color: '#0f172a', marginBottom: 20 }}>
                  More Bonds of Brotherhood Recordings
                </h3>
                <div className="mih-playlist-grid" style={{ '--card-bg': '#fff' } as React.CSSProperties}>
                  {[
                    { title: 'Brotherhood & Accountability', desc: 'On holding one another to the standard of the Quran and Sunnah in daily life.' },
                    { title: 'The Muslim Man in the West', desc: 'Navigating identity, faith, and community as a Muslim man in America.' },
                    { title: 'Fatherhood in Islam', desc: 'The responsibilities and spiritual dimensions of Islamic fatherhood.' },
                    { title: 'Brotherhood & Economic Justice', desc: 'How Islamic brotherhood manifests in economic solidarity and mutual support.' },
                  ].map((talk, i) => (
                    <article key={i} style={{ background: '#fff', border: '1px solid rgba(30,58,138,0.08)', padding: 20 }}>
                      <h4 style={{ fontFamily: "'Amiri', serif", fontSize: 18, color: '#0f172a', margin: '0 0 8px' }}>{talk.title}</h4>
                      <p style={{ fontSize: 13, color: 'rgba(15,23,42,0.5)', lineHeight: 1.6, margin: '0 0 12px' }}>{talk.desc}</p>
                      <a
                        href="https://www.youtube.com/watch?v=8a7awUunJ4s"
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{ fontFamily: 'monospace', fontSize: 9, letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(30,58,138,0.5)', textDecoration: 'none' }}
                        aria-label={`Watch ${talk.title} on YouTube (opens in new tab)`}
                      >
                        ▶ Watch on YouTube
                      </a>
                    </article>
                  ))}
                </div>
              </div>
            </div>
          </section>

          {/* 5. Community Events */}
          <section id="community-events" aria-labelledby="community-events-heading" style={{ background: '#fff', borderTop: '1px solid rgba(30,58,138,0.06)' }}>
            <div className="mih-media-section">
              <span className="mih-media-sec-label">⬡ AL-JAMI — الْجَامِع · The Gatherer</span>
              <p className="mih-media-sec-ayat">
                <span lang="ar">وَلْتَكُن مِّنكُمْ أُمَّةٌ يَدْعُونَ إِلَى الْخَيْرِ</span>
                {' '}&ldquo;And let there be among you a community inviting to good.&rdquo; — Quran 3:104
              </p>
              <h2 id="community-events-heading" className="mih-media-sec-h2">Community Event Videos</h2>
              <p className="mih-media-sec-body">
                Recordings from Eid celebrations, Sunday Feeding Programs, Iftar on Wheels, IIUA forums, and other community events at Masjid Al-Ihsan.
              </p>
              <div className="mih-events-grid" role="list" aria-label="Community event videos">
                {COMMUNITY_EVENTS.map((ev, i) => (
                  <article key={i} className="mih-event-card" role="listitem">
                    <div className="mih-event-card-video" aria-label={`${ev.title} video`}>
                      <iframe
                        src={`https://www.youtube.com/embed/${ev.videoId}`}
                        title={ev.title}
                        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                        allowFullScreen
                        aria-label={`Video: ${ev.title}`}
                      />
                    </div>
                    <div className="mih-event-card-body">
                      <span className="mih-event-card-date">{ev.date}</span>
                      <h3 className="mih-event-card-title">{ev.title}</h3>
                      <p className="mih-event-card-desc">{ev.desc}</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Submit CTA */}
          <div className="mih-media-cta-wrap">
            <div className="mih-media-cta-inner">
              <div className="mih-media-cta-text">
                <h3>Submit Media or Request Content</h3>
                <p>To submit recordings, request a specific Khutbah, or inquire about media partnerships, contact the Masjid directly.</p>
              </div>
              <Link href="/subhaal/masjid-al-ihsan/contact" className="mih-media-cta-btn" aria-label="Contact Masjid Al-Ihsan about media">
                Contact the Masjid →
              </Link>
            </div>
          </div>

        </main>
      </div>
    </>
  );
}
